import { Checkmark, View, ViewOff } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import {
  Body,
  Button,
  Icon,
  Input,
  InputButton,
  InputWrapper,
} from 'vet-bones/design-system/components';

interface SecretInputProps {
  cancelTestId?: string;
  cancelText: string;
  className?: string;
  configuredText: string;
  hasExistingValue?: boolean;
  helpText: string;
  initialValue?: string;
  inputTestId?: string;
  label: string;
  onReplace: (value: string) => void;
  replaceTestId?: string;
  replaceText: string;
  required?: boolean;
  viewTestId?: string;
}

export const SecretInput: React.VFC<SecretInputProps> = ({
  cancelTestId,
  cancelText,
  className,
  configuredText,
  hasExistingValue,
  helpText,
  initialValue = '',
  inputTestId,
  label,
  onReplace,
  replaceTestId,
  replaceText,
  required,
  viewTestId,
}) => {
  const [value, setValue] = React.useState(initialValue);
  const [replacing, setReplacing] = React.useState(false);
  const [view, setView] = React.useState(false);

  React.useEffect(() => {
    if (hasExistingValue) {
      setValue(initialValue);
      setReplacing(false);
      setView(false);
    }
  }, [hasExistingValue, initialValue]);

  if (!hasExistingValue) {
    return (
      <Input
        className={classNames('secret-input', className)}
        clearable={false}
        label={label}
        onChange={(e) => onReplace(e.target.value)}
        required={required}
        rightElement={
          <InputButton
            icon={view ? ViewOff : View}
            onClick={() => setView(!view)}
            testId={viewTestId}
          />
        }
        size="md"
        testId={inputTestId}
        type={view ? 'text' : 'password'}
        value={initialValue}
      />
    );
  }

  if (replacing) {
    return (
      <Input
        className={classNames('secret-input', className)}
        clearable={false}
        helpProps={{
          text: (
            <div className="secret-input-help">
              {helpText}
              <div>
                <Button
                  onClick={() => setReplacing(false)}
                  size="sm"
                  testId={cancelTestId}
                  variant="ghost-gray"
                >
                  {cancelText}
                </Button>
                <Button
                  onClick={() => {
                    setReplacing(false);
                    onReplace(value);
                  }}
                  size="sm"
                  testId={replaceTestId}
                  variant="ghost-gray"
                >
                  {replaceText}
                </Button>
              </div>
            </div>
          ),
        }}
        label={label}
        onChange={(e) => setValue(e.target.value)}
        required={required}
        rightElement={
          <InputButton
            icon={view ? ViewOff : View}
            onClick={() => setView(!view)}
            testId={viewTestId}
          />
        }
        size="md"
        testId={inputTestId}
        type={view ? 'text' : 'password'}
        value={value}
      />
    );
  }

  return (
    <InputWrapper
      className={classNames('secret-input', className)}
      classNamePrefix="vb-input"
      label={label}
      labelSize="md"
      size="md"
    >
      <div className="secret-input-help">
        <div className="secret-input-help-text">
          <Body size="sm" weight="semibold">
            {configuredText}
          </Body>
          <Icon IconComponent={Checkmark} size="sm" />
        </div>
        <Button
          onClick={() => setReplacing(true)}
          size="sm"
          testId={replaceTestId}
          variant="ghost-gray"
        >
          {replaceText}
        </Button>
      </div>
    </InputWrapper>
  );
};
